import { useEffect, useState } from "react";

// react-router-dom components
import { useLocation, NavLink, useNavigate } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import {
  List,
  Divider,
  Link,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  DialogContentText,
  Icon,
} from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDButton from "theme/components/MDButton";

// Material Dashboard 2 React example components
import SidenavCollapse from "app/Navbars/Sidenav/SidenavCollapse";

// Custom styles for the Sidenav
import SidenavRoot from "app/Navbars/Sidenav/SidenavRoot";
import sidenavLogoLabel from "app/Navbars/Sidenav/styles/sidenav";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from "context";

// I added this for authentcation
import { useSignOut, useAuthUser } from "react-auth-kit";

// Install Prompt
import useIosInstallPrompt from "app/hooks/iosShowInstallPrompt";
import useWebInstallPrompt from "app/hooks/webShowInstallPrompt";

function Sidenav({ color, brand, brandName, routes, ...rest }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode } = controller;
  const location = useLocation();
  const collapseName = location.pathname.replace("/", "");

  let textColor = "white";

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
      setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav);
    }

    /** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = routes.map(({ type, name, icon, title, noCollapse, key, href, route }) => {
    let returnValue;

    if (type === "collapse") {
      returnValue = href ? (
        <Link
          href={href}
          key={key}
          target="_blank"
          rel="noreferrer"
          sx={{ textDecoration: "none" }}
        >
          <SidenavCollapse
            name={name}
            icon={icon}
            active={key === collapseName}
            noCollapse={noCollapse}
          />
        </Link>
      ) : (
        <NavLink key={key} to={route}>
          <SidenavCollapse name={name} icon={icon} active={key === collapseName} />
        </NavLink>
      );
    } else if (type === "title") {
      returnValue = (
        <MDTypography
          key={key}
          color={textColor}
          display="block"
          variant="caption"
          fontWeight="bold"
          textTransform="uppercase"
          pl={3}
          mt={2}
          mb={1}
          ml={1}
        >
          {title}
        </MDTypography>
      );
    } else if (type === "divider") {
      returnValue = (
        <Divider
          key={key}
          light={
            (!darkMode && !whiteSidenav && !transparentSidenav) ||
            (darkMode && !transparentSidenav && whiteSidenav)
          }
        />
      );
    } else if (type === "group") {
      returnValue = (
        <MDBox mt={1}>
          <Divider
            // key={key}
            light={
              (!darkMode && !whiteSidenav && !transparentSidenav) ||
              (darkMode && !transparentSidenav && whiteSidenav)
            }
          />
          <MDBox display="flex" justifyContent="space-between" pr={2}>
            <MDBox>
              <MDTypography
                key={key}
                color={textColor}
                display="block"
                variant="caption"
                fontWeight="medium"
                textTransform="uppercase"
                pl={3}
                mt={0.5}
                mb={1}
                ml={1}
              >
                {title}
              </MDTypography>
            </MDBox>
            <MDBox mt={-0.1}>
              <MDTypography color={textColor} display="block" fontWeight="light" fontSize={18}>
                <Icon>{icon}</Icon>
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      );
    }

    return returnValue;
  });

  const auth = useAuthUser();
  const accountInfo = auth();

  const signOut = useSignOut();
  const navigate = useNavigate();
  const authSignOut = () => {
    signOut();
    navigate("/sign-in");
  };

  const [iosInstallPrompt, handleIOSInstallDeclined] = useIosInstallPrompt();
  const [webInstallPrompt, handleWebInstallDeclined, handleWebInstallAccepted] =
    useWebInstallPrompt();

  const [showWebInstallDialog, setShowWebInstallDialog] = useState(false);
  const [showIOSInstallDialog, setShowIOSInstallDialog] = useState(false);

  function showInstallDialog() {
    if (!iosInstallPrompt && !webInstallPrompt) {
      return;
    }

    if (iosInstallPrompt) {
      setShowIOSInstallDialog(true);
    }
    if (webInstallPrompt) {
      setShowWebInstallDialog(true);
    }
  }

  // function visibleZoho(zoho) {
  //   zoho.salesiq.floatwindow.visible("show");
  // }

  // function showZohoWidget() {
  //   window.$zoho = window.$zoho || {};
  //   window.$zoho.salesiq = window.$zoho.salesiq || {
  //     widgetcode: "77efd857844a34fc6c8a49497ba3cec9564570e0b9a018babad998ffe921ae42",
  //     values: {},
  //     ready: visibleZoho(window.$zoho),
  //   };
  //   const d = document;
  //   const s = d.createElement("script");
  //   s.type = "text/javascript";
  //   s.id = "zsiqscript";
  //   s.defer = true;
  //   s.src = "https://salesiq.zoho.com/widget";
  //   const t = d.getElementsByTagName("script")[0];
  //   t.parentNode.insertBefore(s, t);
  //   // d.write("<div id='zsiqwidget'></div>");

  //   return <div id="zsiqwidget"> </div>;
  // }

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
    >
      <MDBox pt={3} pb={1} px={4} textAlign="center">
        <MDBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <MDTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </MDTypography>
        </MDBox>
        <MDBox component={NavLink} to="/" display="flex" alignItems="center">
          {brand && <MDBox component="img" src={brand} alt="Brand" width="2rem" />}
          <MDBox
            width={!brandName && "100%"}
            sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}
          >
            <MDTypography component="h6" variant="button" fontWeight="medium" color={textColor}>
              {brandName}
            </MDTypography>
          </MDBox>
        </MDBox>
        <Divider
          light={
            (!darkMode && !whiteSidenav && !transparentSidenav) ||
            (darkMode && !transparentSidenav && whiteSidenav)
          }
        />
        {accountInfo !== null && (
          <MDBox mt={2} align="left" sx={{ width: "10px" }}>
            <MDBox>
              <MDTypography variant="caption" fontWeight="light" color={textColor}>
                Account
              </MDTypography>
            </MDBox>
            <div>
              <MDBox pr={3}>
                <MDTypography
                  variant="subtitle2"
                  fontWeight="medium"
                  fontSize={14}
                  color={textColor}
                >
                  {accountInfo.account.title}
                </MDTypography>
              </MDBox>
            </div>
          </MDBox>
        )}
      </MDBox>
      <Divider
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
      />
      <List>{renderRoutes}</List>
      <MDBox onClick={() => authSignOut()}>
        <SidenavCollapse name="Sign Out" icon={<Icon fontSize="small">logout</Icon>} />
      </MDBox>
      <Divider />
      <Dialog open={showIOSInstallDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          <MDBox>Install App</MDBox>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <MDBox>
              Tap the browser menu icon <Icon>install_desktop</Icon>&nbsp; then &quot;Add to Home
              Screen&quot;
            </MDBox>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              handleIOSInstallDeclined();
              setShowWebInstallDialog(false);
            }}
            color="warning"
            variant="outlined"
          >
            Cancel
          </MDButton>
        </DialogActions>
      </Dialog>
      <Dialog open={showWebInstallDialog} maxWidth="xs">
        <DialogTitle>
          <MDBox>Install App</MDBox>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <MDBox>You will be prompted to Install App.</MDBox>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              handleWebInstallDeclined();
              setShowWebInstallDialog(false);
            }}
            color="warning"
            variant="outlined"
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={() => {
              setShowWebInstallDialog(false);
              handleWebInstallAccepted();
            }}
            color="success"
            variant="outlined"
          >
            Install
          </MDButton>
        </DialogActions>
      </Dialog>
      <MDBox onClick={() => showInstallDialog()}>
        <SidenavCollapse name="Install App" icon={<Icon fontSize="small">install_mobile</Icon>} />
      </MDBox>
      {/* <Divider />
      <MDBox onClick={() => showZohoWidget()}>
        <SidenavCollapse name="Chat" icon={<Icon fontSize="small">chat</Icon>} />
      </MDBox> */}
    </SidenavRoot>
  );
}

// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
  color: "warning",
  brand: "",
};

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
